import React, { useCallback, useMemo } from 'react';
import { Flex, Text, Button, useDisclosure } from '@televet/kibble-ui';
import { formatTimesStringToDateStringWithTimeZone } from 'pages/Settings/pages/AppointmentSettings/components/DirectBookingSettings/utils';
import { useFindFirstPayoutBatchingPeriodQuery, useGetPayoutBatchingSettingQuery } from 'shared/types/graphql';
import EODModal from './EODModal';
import { ReportingPaymentsReportEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';
import { EODSettings } from './EODSettings';
import addDays from 'date-fns/addDays';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';

interface IEODBannerProps {
  pimsName: string;
  currentClinicId?: string;
  clinicUserId?: string;
  isEODAutomated?: boolean;
}

const EODBanner = ({ pimsName, currentClinicId, clinicUserId, isEODAutomated }: IEODBannerProps): JSX.Element => {
  const { isOpen: isEODModalOpen, onOpen: onEODModalOpen, onClose: onEODModalClose } = useDisclosure();
  const { isOpen: isEODSettingsOpen, onOpen: onEODSettingsOpen, onClose: onEODSettingsClose } = useDisclosure();
  const { trackEvent } = useTracking();
  const { isFeatureEnabled } = useFeatureFlag();

  const { data: payoutBatchingPeriodsData, refetch: refetchFindFirstPayoutBatchingPeriod } =
    useFindFirstPayoutBatchingPeriodQuery({
      variables: {
        where: {
          clinicId: { equals: currentClinicId },
        },
      },
      skip: !currentClinicId,
    });

  const mostRecentPayoutPeriod = payoutBatchingPeriodsData?.findManyPayoutBatchingPeriod?.[0];
  // mutation is to find most recent payout period so only 1 or 0 records are returned

  const endsAt = useMemo(() => {
    if (mostRecentPayoutPeriod?.endsAt) {
      return mostRecentPayoutPeriod.endsAt;
    }
    // if payoutBatchingPeriods data is empty, set endsAt to yesterdays date at 11:59:59 PM
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    return yesterday.toISOString();
  }, [mostRecentPayoutPeriod?.endsAt]);

  const showEODButton = useMemo(() => {
    // return true if there is no mostRecentPayoutPeriod or if the mostRecentPayoutPeriod endsAt is before today (dont show if it is the same day)
    const today = new Date();
    // set todays date to 00:00:00 AM
    today.setHours(0, 0, 0, 0);
    return !mostRecentPayoutPeriod || new Date(mostRecentPayoutPeriod.endsAt) < today;
  }, [mostRecentPayoutPeriod]);

  const handleEndDayClick = useCallback(() => {
    trackEvent(ReportingPaymentsReportEvents.PayEodStart);
    onEODModalOpen();
  }, [onEODModalOpen, trackEvent]);

  const {
    data: payoutBatchingSettingData,
    loading: loadingPayoutBatchingSetting,
    refetch: refetchPayoutBatchingSetting,
  } = useGetPayoutBatchingSettingQuery({
    variables: {
      where: {
        clinicId: {
          equals: currentClinicId,
        },
      },
    },
    skip: !currentClinicId,
  });

  const payoutBatchingSettings = useMemo(
    () => payoutBatchingSettingData?.findFirstPayoutBatchingSetting,
    [payoutBatchingSettingData],
  );

  const nextPayoutPeriod = useMemo(() => {
    const sortedSchedules = [...(payoutBatchingSettings?.schedules || [])].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    if (!sortedSchedules.length) {
      return '';
    }

    const today = new Date();
    const lastPayoutBatchingPeriodEndsAt = new Date(mostRecentPayoutPeriod?.endsAt || '');

    // If the most recent payout period ends today, add 1 day to today
    const isLastPayoutBatchingPeriodToday = today.toDateString() === lastPayoutBatchingPeriodEndsAt.toDateString();
    const daysToAdd = isLastPayoutBatchingPeriodToday ? 1 : 0;

    const nextPayoutBatchingPeriod = addDays(today, daysToAdd);
    const nextPayoutBatchingDay = nextPayoutBatchingPeriod.getDay();

    const nextSchedule = sortedSchedules?.find((schedule) => schedule.dayOfWeek === nextPayoutBatchingDay);

    if (nextSchedule) {
      const nextScheduleEndTime = new Date(nextSchedule.endTime);
      const targetDate = nextPayoutBatchingPeriod;
      targetDate.setHours(nextScheduleEndTime.getHours(), nextScheduleEndTime.getMinutes(), 0, 0);
      return formatTimesStringToDateStringWithTimeZone(targetDate.toISOString());
    }

    // If there's no schedule for today, iterate through the schedules until we find the next one
    let nextAvailableSchedule = null;
    let currentDay = (nextPayoutBatchingDay + 1) % 7;
    let daysAhead = 1;

    while (daysAhead <= 7) {
      nextAvailableSchedule = sortedSchedules.find((schedule) => schedule.dayOfWeek === currentDay);

      if (nextAvailableSchedule) {
        break;
      }

      currentDay = (currentDay + 1) % 7;
      daysAhead++;
    }

    if (nextAvailableSchedule) {
      const nextAvailableScheduleEndTime = new Date(nextAvailableSchedule.endTime);
      const targetDate = addDays(nextPayoutBatchingPeriod, daysAhead);
      targetDate.setHours(nextAvailableScheduleEndTime.getHours(), nextAvailableScheduleEndTime.getMinutes(), 0, 0);
      return formatTimesStringToDateStringWithTimeZone(targetDate.toISOString());
    }

    return '';
  }, [mostRecentPayoutPeriod?.endsAt, payoutBatchingSettings]);

  const handleCloseSettingsModal = useCallback(() => {
    onEODSettingsClose();
    refetchPayoutBatchingSetting();
  }, [onEODSettingsClose, refetchPayoutBatchingSetting]);

  return (
    <>
      <Flex
        backgroundColor="background.lessSubtle"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        borderRadius="sm"
        py={2}
        px={3}
      >
        <Text>
          Last End of Day:{' '}
          <Text as="span" fontWeight="semibold">
            {formatTimesStringToDateStringWithTimeZone(endsAt)}
          </Text>
        </Text>
        <Flex gap={3}>
          {showEODButton && !isEODAutomated && (
            <Button size="xs" variant="secondarySubtle" onClick={handleEndDayClick}>
              End Day
            </Button>
          )}
          {isEODAutomated && !!nextPayoutPeriod && (
            <Text as="p" variant="subtle">
              Next End of Day:{' '}
              <Text as="span" fontWeight="semibold" variant="subtle">
                {nextPayoutPeriod}
              </Text>
            </Text>
          )}
          {isFeatureEnabled(FeatureFlagName.AutoEODPayoutControls) && (
            <Button
              size="xs"
              variant="secondary"
              iconName="pen"
              onClick={onEODSettingsOpen}
              isLoading={loadingPayoutBatchingSetting}
            >
              Edit
            </Button>
          )}
        </Flex>
      </Flex>
      <EODModal
        previousEndsAt={endsAt}
        isOpen={isEODModalOpen}
        onClose={onEODModalClose}
        pimsName={pimsName}
        clinicId={currentClinicId}
        clinicUserId={clinicUserId}
        refreshUI={refetchFindFirstPayoutBatchingPeriod}
      />
      <EODSettings
        isOpen={isEODSettingsOpen}
        onClose={handleCloseSettingsModal}
        settings={payoutBatchingSettings}
        pimsName={pimsName}
      />
    </>
  );
};

export default EODBanner;
